<script setup lang="ts">
// types
import type { BlockQuoteProps } from '@ts-blocks/block-quote/interfaces'
// hooks
import { useBlockQuote } from '@hooks/blocks/block-quote.hook'
// helpers
import { STRAPI_IMAGE_SIZES } from '@helpers/strapi-media'
// assets
import quoteMark from '@images/sections/quote/quote-mark.svg'
// components
import { AppImage } from '@UI/Images/AppImage'
import BlockTextHighlightRow from '@Blocks/BlockTextHighlightRow.vue'

defineOptions({
  name: 'BlockQuote',
})

const props = withDefaults(defineProps<{
  block: BlockQuoteProps
  surface?: 'brand' | 'tertiary'
}>(), {
  surface: 'brand',
})

const {
  refImageAlt,
} = useBlockQuote({
  block: () => props.block,
})
</script>

<template>
  <article
    class="block-quote"
    :class="`block-quote--${props.surface}`"
  >
    <template v-if="props.surface === 'brand'">
      <span
        class="block-quote__shape-box block-quote__shape-box--top"
        aria-hidden="true"
      >
        <span class="block-quote__shape block-quote__shape--top" />
      </span>
      <span
        class="block-quote__shape-box block-quote__shape-box--bottom"
        aria-hidden="true"
      >
        <span class="block-quote__shape block-quote__shape--bottom" />
      </span>
    </template>

    <div class="block-quote__card">
      <blockquote class="block-quote__quote">
        <div class="block-quote__text">
          <img
            class="block-quote__mark"
            :src="quoteMark"
            alt=""
            width="44"
            height="36"
          >
          <p
            v-if="block.title"
            class="block-quote__title btly-text-title-xl md:btly-text-heading-s"
          >
            <BlockTextHighlightRow :block="block.title" />
          </p>
        </div>

        <footer class="block-quote__author">
          <AppImage
            v-if="block.image"
            class="block-quote__avatar"
            :media="block.image"
            :sizes="STRAPI_IMAGE_SIZES"
            :alt="refImageAlt"
          />
          <span
            v-else
            class="block-quote__avatar"
            aria-hidden="true"
          />
          <div class="block-quote__person">
            <span class="block-quote__name btly-text-title-m">{{ block.personName }}</span>
            <span class="block-quote__position btly-text-title-s">{{ block.position }}</span>
          </div>
        </footer>
      </blockquote>

      <p
        v-if="block.description"
        class="block-quote__body btly-text-body-l"
      >{{ block.description }}</p>
    </div>
  </article>
</template>

<style scoped lang="scss" src="@scss/components/Blocks/BlockQuote.scss" />
